/** Default matches `scroll-reveal` so the clip starts in a similar “scroll” window. */
const VIDEO_IO_ROOT_MARGIN = "0px 0px -8% 0px";

/**
 * Muted background clip: load/play when `[data-perspectives]` scrolls into view, pause
 * when it leaves (same rootMargin as scroll-reveal). No autoplay for reduced motion.
 */
export function initPerspectivesVideoOnScroll(section) {
  const video = section.querySelector(".perspectives__video");
  if (!video) return;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    },
    { root: null, rootMargin: VIDEO_IO_ROOT_MARGIN, threshold: 0 },
  );
  io.observe(section);
}

/**
 * Perspective connector lines: one <path> per item, same coordinate system as before.
 * End dots: `[data-perspective-line-end="n"]`; starts: nth `.perspectives__item` headline.
 */
export function initPerspectivesLinesBase(section) {
  const svg = section.querySelector("[data-perspectives-svg]");
  if (!svg) return;

  const LINE_GAP_PX = 16;

  const connectors = [1, 2, 3].map((n) => ({
    path: section.querySelector(`[data-perspective-line="${n}"]`),
    start: section.querySelector(
      `.perspectives__item:nth-child(${n}) .perspectives__item-headline`,
    ),
    end: section.querySelector(`[data-perspective-line-end="${n}"]`),
  }));

  if (connectors.some((c) => !c.path || !c.start || !c.end)) return;

  function update() {
    const bounds = section.getBoundingClientRect();
    const w = Math.max(0, bounds.width);
    const h = Math.max(0, bounds.height);
    if (w < 1 || h < 1) return;

    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    svg.setAttribute("width", String(w));
    svg.setAttribute("height", String(h));

    const sr = section.getBoundingClientRect();

    for (const { path, start, end } of connectors) {
      const hr = start.getBoundingClientRect();
      const er = end.getBoundingClientRect();

      const sx = hr.right - sr.left + LINE_GAP_PX;
      const sy = hr.top + hr.height / 2 - sr.top;
      const ex = er.left + er.width / 2 - sr.left;
      const ey = er.top + er.height / 2 - sr.top;

      path.setAttribute("d", `M ${sx} ${sy} L ${ex} ${ey}`);
    }
  }

  const ro = new ResizeObserver(update);
  ro.observe(section);
  window.addEventListener("resize", update);
  if (document.fonts?.ready) {
    document.fonts.ready.then(update);
  }
  update();
}
